import { getSessionStorage } from '@/shared/lib/storage';
import { isDebugEnabled } from '@/shared/lib/system';
import type { ReleaseFlags, ReleaseFlagType } from './releaseFlag';
import { useSessionStore } from './sessionStore';

/**
 * 特定のリリースフラグが有効かどうかを取得する（React hook 版）
 *
 * 解決順: デバッグオーバーライド → API取得値 → デフォルト(false)
 * 未定義のフラグは安全側に倒して `false`（未リリース扱い）。
 */
export const useReleaseFlag = (releaseFlagType: ReleaseFlagType): boolean => {
  const releaseFlags = useSessionStore((state) => state.releaseFlags);
  return resolveReleaseFlag({ releaseFlagType, releaseFlags });
};

/**
 * リリースフラグの状態を取得する（非hook版）
 * `beforeLoad` 等の非コンポーネントコンテキストから使用
 */
export const isReleaseFlagEnabled = (releaseFlagType: ReleaseFlagType): boolean => {
  const releaseFlags = useSessionStore.getState().releaseFlags;
  return resolveReleaseFlag({ releaseFlagType, releaseFlags });
};

/**
 * リリースフラグの状態を解決する内部関数
 *
 * デバッグオーバーライド (dev/local のみ) → API取得値 → デフォルト(false)
 */
const resolveReleaseFlag = ({
  releaseFlagType,
  releaseFlags,
}: {
  releaseFlagType: ReleaseFlagType;
  releaseFlags: ReleaseFlags;
}): boolean => {
  if (isDebugEnabled()) {
    const overrides = getSessionStorage('debug-release-flags');
    const override = overrides?.[releaseFlagType];
    if (typeof override === 'boolean') {
      return override;
    }
  }

  return releaseFlags[releaseFlagType] ?? false;
};
